import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserGroup,
  faQrcode,
  faPhone,
  faPhoneAlt,
  faHome,
  faIdCard,
  faUser,
  faUsers,
  faMagic,
  faPlusCircle,
  faCheckCircle,
  faExclamationCircle,
  faTimesCircle,
  faInfoCircle,
  faSave,
  faRefresh,
  faAddressCard,
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faSort,
  faEye,
  faPlus,
  faTimes,
  faChevronRight,
  faChevronLeft,
  faDatabase,
  faSync,
  faChartLine,
  faClipboardList,
  faMale,
  faFemale,
  faChild,
  faUsersCog,
  faCheck,
  faArrowRight,
  faCalendar,
  faLocationDot,
  faFileExport,
  faUpload,
  faDownload,
  faEllipsisVertical,
  faCircleInfo,
  faUserEdit,
  faTrashCan,
  faUserPlus,
  faClock,
  faVenus,
  faMars,
  faUserFriends,
  faMapMarkerAlt,
  faEnvelope,
  faUserCheck,
  faUserClock,
  faExclamationTriangle,
  faBan,
  faShieldAlt,
  faDatabase as faDatabaseSolid,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import "./addFamily.css";
import {
  API_BASE_URL,
  FAMILIES_API_URL,
  VALIDATION_PATTERNS,
  debounce,
  toDigitsOnly,
  formatCnicDisplay,
  formatMobileDisplay,
  DuplicatePreventionModal,
} from "./addFamilyhelpers";

// ============================================
// MAIN COMPONENT
// ============================================
const FamilyManager = () => {
  // ============================================
  // FORM STATES
  // ============================================
  const [formData, setFormData] = useState({
    familyName: "",
    familyCode: "",
    primaryMobile: "",
    secondaryMobile: "",
    address: "",
    cnic: "",
    boys: 1,
    girls: 0,
    totalStudents: 1,
  });

  const [formErrors, setFormErrors] = useState({
    familyName: "",
    familyCode: "",
    primaryMobile: "",
    secondaryMobile: "",
    cnic: "",
    address: "",
    boys: "",
    girls: "",
  });

  const [fieldValidation, setFieldValidation] = useState({
    cnic: { isValid: false, isChecking: false, lastChecked: null },
    primaryMobile: { isValid: false, isChecking: false, lastChecked: null },
    secondaryMobile: { isValid: false, isChecking: false, lastChecked: null },
  });

  // ============================================
  // UI STATES
  // ============================================
  const [families, setFamilies] = useState([]);
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [familyStudents, setFamilyStudents] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showFamilyDetails, setShowFamilyDetails] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const [duplicateData, setDuplicateData] = useState(null);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [validationHistory, setValidationHistory] = useState([]);

  // ============================================
  // AXIOS CONFIGURATION
  // ============================================
  const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    withCredentials: false,
    timeout: 10000,
  });

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const logValidationEvent = useCallback((type, data) => {
    const event = {
      type,
      timestamp: new Date().toISOString(),
      data,
      userAction: window.location.pathname,
    };
    setValidationHistory(prev => [event, ...prev.slice(0, 9)]);
  }, []);

  // ============================================
  // STATUS & PROGRESS HELPERS (UPDATED WITH FALLBACK)
  // ============================================
  const getStatusInfo = (family) => {
    const registered = family.registered_students || 0;
    // 🛡️ FIX: Fallback to total_students if boys+girls sum is 0
    const total = (parseInt(family.boys) || 0) + (parseInt(family.girls) || 0) || parseInt(family.total_students) || 0;

    if (registered === 0) {
      return { 
        text: "Pending", 
        color: "#6b7280", 
        icon: faClock,
        bgColor: "rgba(107, 114, 128, 0.1)"
      };
    }
    if (registered >= total) {
      return { 
        text: "Complete", 
        color: "#10b981", 
        icon: faCheckCircle,
        bgColor: "rgba(16, 185, 129, 0.1)"
      };
    }
    return { 
      text: "In process", 
      color: "#f59e0b", 
      icon: faExclamationCircle,
      bgColor: "rgba(245, 158, 11, 0.1)"
    };
  };

  const getRegistrationProgress = (family) => {
    const registered = family.registered_students || 0;
    // 🛡️ FIX: Fallback to total_students if boys+girls sum is 0
    const total = (parseInt(family.boys) || 0) + (parseInt(family.girls) || 0) || parseInt(family.total_students) || 0;
    const progress = total > 0 ? Math.min(Math.round((registered / total) * 100), 100) : 0;
    return progress;
  };

  const showSuccess = (message) => {
    Swal.fire({
      icon: "success",
      title: "Success!",
      text: message,
      timer: 2000,
      showConfirmButton: false,
      position: "top-end",
      toast: true,
    });
  };

  const showError = (title, message = "") => {
    Swal.fire({
      icon: "error",
      title: title,
      html: message ? `<div class="error-message-content">${message}</div>` : undefined,
      confirmButtonText: "OK",
      confirmButtonColor: "#d33",
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp'
      }
    });
  };

  const showWarning = (title, message) => {
    Swal.fire({
      icon: "warning",
      title: title,
      text: message,
      confirmButtonText: "OK",
      confirmButtonColor: "#f59e0b",
    });
  };

  // ============================================
  // DUPLICATE CHECKING
  // ============================================
  const checkForDuplicates = useCallback(async (data, isEditing = false) => {
    try {
      setIsCheckingDuplicates(true);
      
      const checkData = {
        cnic: data.cnic,
        primaryMobile: data.primaryMobile,
        secondaryMobile: data.secondaryMobile || "",
      };

      if (checkData.secondaryMobile === "") {
        delete checkData.secondaryMobile;
      }

      if (isEditing && selectedFamily) {
        checkData.exclude_family_code = selectedFamily.family_code;
      }

      const cacheKey = JSON.stringify(checkData);
      const cachedResult = sessionStorage.getItem(`duplicate_check_${cacheKey}`);
      
      if (cachedResult) {
        const result = JSON.parse(cachedResult);
        if (Date.now() - result.timestamp < 30000) {
          setIsCheckingDuplicates(false);
          return result.hasDuplicates ? result.duplicates : null;
        }
      }

      const formData = new URLSearchParams();
      Object.keys(checkData).forEach(key => {
        formData.append(key, checkData[key]);
      });

      const response = await axiosInstance.post(
        `${FAMILIES_API_URL}?action=check-duplicates-before-submit`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      setIsCheckingDuplicates(false);
      
      if (response.data) {
        sessionStorage.setItem(`duplicate_check_${cacheKey}`, JSON.stringify({
          ...response.data,
          timestamp: Date.now()
        }));
      }

      if (response.data.hasDuplicates) {
        logValidationEvent('DUPLICATE_FOUND', {
          duplicates: response.data.duplicates,
          data: checkData,
          isEditing
        });
        return response.data.duplicates;
      }
      
      logValidationEvent('NO_DUPLICATES', { data: checkData, isEditing });
      return null;
    } catch (error) {
      setIsCheckingDuplicates(false);
      console.error('Error checking duplicates:', error);
      
      logValidationEvent('DUPLICATE_CHECK_ERROR', {
        error: error.message,
        data: data
      });

      const result = await Swal.fire({
        icon: 'question',
        title: 'Duplicate Check Failed',
        text: 'Unable to verify duplicate data. How would you like to proceed?',
        showCancelButton: true,
        confirmButtonText: 'Continue Anyway',
        cancelButtonText: 'Cancel Submission',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#d33',
      });

      if (result.isConfirmed) {
        return null;
      } else {
        throw new Error('User cancelled submission due to duplicate check failure');
      }
    }
  }, [selectedFamily, logValidationEvent]);

  const debouncedDuplicateCheck = useCallback(
    debounce(async (field, value, isEditing) => {
      if (!value.trim() || !['cnic', 'primaryMobile', 'secondaryMobile'].includes(field)) {
        return;
      }

      setFieldValidation(prev => ({
        ...prev,
        [field]: { ...prev[field], isChecking: true }
      }));

      try {
        const formData = new URLSearchParams();
        formData.append(field, value);
        
        if (isEditing && selectedFamily) {
          formData.append('exclude_family_code', selectedFamily.family_code);
        }

        const response = await axiosInstance.post(
          `${FAMILIES_API_URL}?action=check-duplicates-before-submit`,
          formData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (response.data.duplicates && response.data.duplicates[field]) {
          const duplicate = response.data.duplicates[field];
          const fieldName = field === 'cnic' ? 'CNIC' : 
                          field === 'primaryMobile' ? 'Primary Mobile' : 
                          'Secondary Mobile';
          
          setFormErrors(prev => ({
            ...prev,
            [field]: `${fieldName} "${duplicate.value}" already exists for family "${duplicate.existing_family}" (${duplicate.existing_code})`
          }));

          setFieldValidation(prev => ({
            ...prev,
            [field]: { 
              isValid: false, 
              isChecking: false, 
              lastChecked: new Date().toISOString(),
              duplicateInfo: duplicate
            }
          }));

          logValidationEvent('REALTIME_DUPLICATE_FOUND', {
            field,
            value,
            duplicate
          });
        } else {
          if (formErrors[field] && formErrors[field].includes('already exists')) {
            setFormErrors(prev => ({
              ...prev,
              [field]: ""
            }));
          }

          setFieldValidation(prev => ({
            ...prev,
            [field]: { 
              isValid: true, 
              isChecking: false, 
              lastChecked: new Date().toISOString() 
            }
          }));
        }
      } catch (error) {
        console.error(`Error checking ${field} duplicate:`, error);
        setFieldValidation(prev => ({
          ...prev,
          [field]: { 
            ...prev[field], 
            isChecking: false,
            lastChecked: new Date().toISOString()
          }
        }));
      }
    }, 1500),
    [selectedFamily, formErrors, logValidationEvent]
  );

  const handleFieldBlur = async (field, value) => {
    const duplicateCheckFields = ['cnic', 'primaryMobile', 'secondaryMobile'];
    
    if (!duplicateCheckFields.includes(field) || !value.trim()) {
      return;
    }

    if (field === 'secondaryMobile' && value.trim() === "") {
      return;
    }

    if (field === 'cnic' && !VALIDATION_PATTERNS.cnic.test(value)) {
      setFormErrors(prev => ({ ...prev, [field]: "Format: 13 digits (e.g. 3460371064171)" }));
      return;
    }

    if ((field === 'primaryMobile' || field === 'secondaryMobile') && 
        !VALIDATION_PATTERNS.mobile.test(value)) {
      setFormErrors(prev => ({ ...prev, [field]: "Format: 11 digits starting with 03" }));
      return;
    }

    if (formErrors[field] && formErrors[field].includes('Format:')) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }

    debouncedDuplicateCheck(field, value, showEditForm);
  };

  const showDuplicateError = (duplicates) => {
    setDuplicateData(duplicates);
    setShowDuplicateModal(true);
  };

  const handleDuplicateModalConfirm = async () => {
    if (!pendingSubmission) return;
    
    setShowDuplicateModal(false);
    setDuplicateData(null);
    
    const { familyData, isEditing } = pendingSubmission;
    
    try {
      if (isEditing) {
        await updateFamily(selectedFamily.family_code, familyData);
      } else {
        await createFamily(familyData);
      }
    } catch (error) {
      console.error("Forced submission error:", error);
      showError("Submission Failed", "An error occurred while submitting the form.");
    } finally {
      setPendingSubmission(null);
    }
  };

  const handleDuplicateModalCancel = () => {
    setShowDuplicateModal(false);
    setDuplicateData(null);
    setPendingSubmission(null);
    setIsSubmitting(false);
  };

  const handleEditExistingFamily = () => {
    if (!duplicateData) return;
    
    const firstDuplicate = Object.values(duplicateData).find(d => d.exists);
    if (firstDuplicate) {
      fetchFamilyByCode(firstDuplicate.existing_code);
      setShowDuplicateModal(false);
      setDuplicateData(null);
      setPendingSubmission(null);
    }
  };

  // ============================================
  // VALIDATION FUNCTIONS
  // ============================================
  // Custom regex for new family code prefix: MHS:: followed by 5 digits
  const FAMILY_CODE_REGEX = /^MHS::\d{5}$/;

  const validateField = (field, value) => {
    let error = "";
    let isValid = true;

    switch (field) {
      case "familyName":
        if (!value.trim()) {
          error = "Family name is required";
          isValid = false;
        } else if (!VALIDATION_PATTERNS.familyName.test(value)) {
          error = "Enter a valid family name (3-50 characters, letters, spaces, and basic punctuation only)";
          isValid = false;
        }
        break;

      case "familyCode":
        if (!value.trim()) {
          error = "Family code is required";
          isValid = false;
        } else if (!FAMILY_CODE_REGEX.test(value)) {
          error = "Family code must follow pattern: MHS:: followed by 5 digits (e.g. MHS::00001)";
          isValid = false;
        }
        break;

      case "primaryMobile":
        if (!value.trim()) {
          error = "Primary mobile is required";
          isValid = false;
        } else if (!VALIDATION_PATTERNS.mobile.test(value)) {
          error = "Format: 11 digits starting with 03 (e.g. 03001234567)";
          isValid = false;
        }
        break;

      case "secondaryMobile":
        if (value.trim() && !VALIDATION_PATTERNS.mobile.test(value)) {
          error = "Format: 11 digits starting with 03";
          isValid = false;
        }
        break;

      case "cnic":
        if (!value.trim()) {
          error = "CNIC is required";
          isValid = false;
        } else if (!VALIDATION_PATTERNS.cnic.test(value)) {
          error = "Format: 13 digits (e.g. 3460371064171)";
          isValid = false;
        }
        break;

      case "address":
        if (!value.trim()) {
          error = "Address is required";
          isValid = false;
        } else if (value.length < 10) {
          error = "Address is too short (minimum 10 characters)";
          isValid = false;
        } else if (value.length > 500) {
          error = "Address is too long (maximum 500 characters)";
          isValid = false;
        }
        break;

      case "boys":
      case "girls":
        const numValue = parseInt(value) || 0;
        if (numValue < 0) {
          error = "Cannot be negative";
          isValid = false;
        } else if (numValue > 50) {
          error = "Maximum 50 students per gender";
          isValid = false;
        }
        break;
    }

    setFormErrors(prev => ({ ...prev, [field]: error }));
    return isValid;
  };

  const validateForm = () => {
    const fields = [
      "familyName",
      "familyCode",
      "primaryMobile",
      "cnic",
      "address",
      "boys",
      "girls",
    ];
    
    let isValid = true;

    fields.forEach((field) => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    if (formData.secondaryMobile && !validateField("secondaryMobile", formData.secondaryMobile)) {
      isValid = false;
    }

    const totalStudents = (parseInt(formData.boys) || 0) + (parseInt(formData.girls) || 0);
    if (totalStudents < 1) {
      setFormErrors(prev => ({
        ...prev,
        boys: "At least one student (boy or girl) is required",
        girls: "At least one student (boy or girl) is required"
      }));
      isValid = false;
    }

    const hasDuplicateErrors = Object.keys(formErrors).some(key => 
      formErrors[key] && formErrors[key].includes('already exists')
    );

    if (hasDuplicateErrors) {
      showError(
        "Duplicate Data Errors",
        "Please resolve all duplicate data errors before submitting."
      );
      isValid = false;
    }

    return isValid;
  };

  // ============================================
  // API FUNCTIONS
  // ============================================
  const generateFamilyCode = async () => {
    setIsGeneratingCode(true);
    try {
      const response = await axiosInstance.get(FAMILIES_API_URL, {
        params: { 
          action: 'get-last-family-code',
          _: Date.now()
        }
      });

      let lastCode = response.data.last_family_code || 'MHS::00000';
      
      const match = lastCode.match(/MHS::(\d+)/);
      let nextNumber = 1;
      
      if (match && match[1]) {
        const currentNumber = parseInt(match[1]);
        if (!isNaN(currentNumber)) {
          nextNumber = currentNumber + 1;
        }
      }
      
      const newNumber = nextNumber.toString().padStart(5, '0');
      const newCode = `MHS::${newNumber}`;
      
      const exists = families.some(f => f.family_code === newCode);
      if (exists) {
        nextNumber++;
        const fallbackCode = `MHS::${nextNumber.toString().padStart(5, '0')}`;
        handleInputChange("familyCode", fallbackCode);
        showWarning(
          "Code Adjusted",
          `Family code adjusted to ${fallbackCode} to avoid conflict.`
        );
      } else {
        handleInputChange("familyCode", newCode);
        showSuccess(`Family Code Generated: ${newCode}`);
      }
      
      return newCode;
    } catch (error) {
      console.error('Error generating family code:', error);
      
      const timestamp = Date.now().toString();
      const random = Math.floor(1000 + Math.random() * 9000);
      const fallbackCode = `MHS::${timestamp.slice(-8)}${random}`;
      
      handleInputChange("familyCode", fallbackCode);
      
      showWarning(
        "Fallback Code Generated",
        `Using fallback code: ${fallbackCode}. Please verify it's unique.`
      );
      
      return fallbackCode;
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const fetchFamilies = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(FAMILIES_API_URL, {
        params: {
          action: "fetch-families",
          _: Date.now(),
        },
        timeout: 10000,
      });

      if (response.data && Array.isArray(response.data.families)) {
        setFamilies(response.data.families);
      } else {
        console.warn("Unexpected response format:", response.data);
        setFamilies([]);
      }
    } catch (error) {
      console.error("Error fetching families:", error);
      showError("Failed to load families", "Please check your connection and try again.");
      setFamilies([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchFamilyDetails = async (familyCode) => {
    try {
      const response = await axiosInstance.get(FAMILIES_API_URL, {
        params: {
          action: "get-family-details",
          family_code: familyCode,
        },
      });

      if (response.data && response.data.family) {
        setSelectedFamily(response.data.family);
        setFamilyStudents(response.data.students || []);
      }
    } catch (error) {
      console.error("Error fetching family details:", error);
      showError(
        "Failed to load complete family details",
        "Showing basic information only."
      );
    }
  };

  const createFamily = async (data) => {
    try {
      setIsSubmitting(true);

      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });

      const response = await axiosInstance.post(FAMILIES_API_URL, formData, {
        params: { action: "create-family" },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.message) {
        showSuccess("Family created successfully!");
        handleSubmitSuccess();
        return true;
      } else {
        throw new Error(response.data?.error || "Failed to create family");
      }
    } catch (error) {
      console.error("Error creating family:", error);
      
      if (error.response?.data?.error?.includes('Duplicate') || 
          error.response?.data?.message?.includes('already exists')) {
        
        const serverDuplicates = error.response.data.duplicates || {};
        if (Object.keys(serverDuplicates).length > 0) {
          showDuplicateError(serverDuplicates);
        } else {
          showError(
            "Duplicate Data",
            "The server detected duplicate data. Please check your information and try again."
          );
        }
      } else if (error.code === 'ERR_NETWORK') {
        showError(
          "Network Error",
          "Cannot connect to the server. Please check your internet connection and try again."
        );
      } else {
        showError(
          "Failed to create family",
          error.response?.data?.error || error.message || "Unknown error occurred"
        );
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFamily = async (familyCode, data) => {
    try {
      setIsSubmitting(true);
      
      const formData = new FormData();
      formData.append("family_code", familyCode);
      
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          formData.append(key, data[key]);
        }
      });
      
      const response = await axiosInstance.post(FAMILIES_API_URL, formData, {
        params: { action: "update-family" },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data && response.data.message) {
        showSuccess("Family updated successfully!");
        handleSubmitSuccess();
        return true;
      } else {
        throw new Error(response.data?.error || "Failed to update family");
      }
    } catch (error) {
      console.error("Error updating family:", error);
      
      if (error.response?.data?.error?.includes('Duplicate') || 
          error.response?.data?.message?.includes('already exists')) {
        
        const serverDuplicates = error.response.data.duplicates || {};
        if (Object.keys(serverDuplicates).length > 0) {
          showDuplicateError(serverDuplicates);
        } else {
          showError(
            "Duplicate Data",
            "The server detected duplicate data. Please check your information and try again."
          );
        }
      } else if (error.code === 'ERR_NETWORK') {
        showError(
          "Network Error",
          "Cannot connect to the server. Please check your internet connection and try again."
        );
      } else {
        let errorMessage = error.message;
        if (error.response?.data) {
          errorMessage = typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data);
        }
        
        showError("Failed to update family", errorMessage);
      }
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteFamily = async (family) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      html: `
        <div class="delete-confirmation">
          <div class="delete-warning-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <p>You are about to delete the family record.</p>
          <h4>${family.family_name} (${family.family_code})</h4>
          <div class="delete-details">
            <p><strong>Warning:</strong> This will only delete the family record.</p>
            <p>Associated students will NOT be deleted but will become solo students.</p>
          </div>
          <p class="delete-final-warning">This action cannot be undone!</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp'
      }
    });

    if (result.isConfirmed) {
      try {
        const formData = new FormData();
        formData.append("family_code", family.family_code);

        const response = await axiosInstance.post(FAMILIES_API_URL, formData, {
          params: { action: "delete-family" },
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.data && response.data.message) {
          showSuccess("Family deleted successfully!");
          fetchFamilies();
          closeAllModals();
          return true;
        } else {
          throw new Error(response.data?.error || "Failed to delete family");
        }
      } catch (error) {
        console.error("Error deleting family:", error);
        
        if (error.response?.data?.error?.includes('existing students')) {
          showError(
            "Cannot Delete Family",
            "This family has existing students. Please remove or reassign all students first."
          );
        } else if (error.code === 'ERR_NETWORK') {
          showError(
            "Network Error",
            "Cannot connect to the server. Please check your internet connection and try again."
          );
        } else {
          showError(
            "Failed to delete family",
            error.response?.data?.error || error.message
          );
        }
        return false;
      }
    }
    return false;
  };

  const fetchFamilyByCode = async (familyCode) => {
    try {
      const response = await axiosInstance.get(FAMILIES_API_URL, {
        params: {
          action: "get-family-details",
          family_code: familyCode,
        },
      });

      if (response.data && response.data.family) {
        const family = response.data.family;
        setFormData({
          familyName: family.family_name || "",
          familyCode: family.family_code || "",
          primaryMobile: family.primary_mobile || "",
          secondaryMobile: family.secondary_mobile || "",
          address: family.address || "",
          cnic: family.cnic || "",
          boys: family.boys || 1,
          girls: family.girls || 0,
          totalStudents: family.total_students || Math.max(1, (family.boys || 0) + (family.girls || 0)),
        });
        setSelectedFamily(family);
        setShowEditForm(true);
        setShowAddForm(false);
        setShowFamilyDetails(false);
        setShowDuplicateModal(false);
      }
    } catch (error) {
      console.error("Error fetching family:", error);
      showError("Failed to load family", "Could not load family details for editing.");
    }
  };

  // ============================================
  // FORM HANDLING
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      logValidationEvent('FORM_VALIDATION_FAILED', {
        errors: formErrors,
        formData: { ...formData, totalStudents: undefined }
      });
      return;
    }

    const totalStudents = (parseInt(formData.boys) || 0) + (parseInt(formData.girls) || 0);
    
    if (totalStudents < 1) {
      showError(
        "Student Count Required", 
        "Family must have at least one student (boy or girl)."
      );
      return;
    }

    if (totalStudents > 100) {
      showError(
        "Too Many Students",
        "Maximum 100 students per family. Please adjust the numbers."
      );
      return;
    }

    const familyData = {
      familyName: formData.familyName.trim(),
      familyCode: formData.familyCode,
      primaryMobile: formData.primaryMobile,
      secondaryMobile: formData.secondaryMobile.trim() || "",
      address: formData.address.trim(),
      cnic: formData.cnic,
      boys: parseInt(formData.boys) || 0,
      girls: parseInt(formData.girls) || 0,
      totalStudents: totalStudents,
    };

    const submissionAttempt = {
      familyData,
      isEditing: showEditForm && selectedFamily ? true : false,
      timestamp: new Date().toISOString()
    };

    setIsSubmitting(true);
    
    try {
      let duplicates = null;
      try {
        duplicates = await checkForDuplicates(
          familyData, 
          submissionAttempt.isEditing
        );
      } catch (duplicateCheckError) {
        if (duplicateCheckError.message === 'User cancelled submission due to duplicate check failure') {
          setIsSubmitting(false);
          return;
        }
        
        const proceedResult = await Swal.fire({
          icon: 'warning',
          title: 'Duplicate Check Failed',
          text: 'Unable to verify duplicate data. Do you want to proceed anyway?',
          showCancelButton: true,
          confirmButtonText: 'Proceed Anyway',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#f59e0b',
          cancelButtonColor: '#d33',
        });
        
        if (!proceedResult.isConfirmed) {
          setIsSubmitting(false);
          return;
        }
      }
      
      if (duplicates) {
        setPendingSubmission(submissionAttempt);
        showDuplicateError(duplicates);
        return;
      }

      if (submissionAttempt.isEditing) {
        await updateFamily(selectedFamily.family_code, familyData);
      } else {
        await createFamily(familyData);
      }

      logValidationEvent('FORM_SUBMISSION_SUCCESS', submissionAttempt);

    } catch (error) {
      console.error("Form submission error:", error);
      
      logValidationEvent('FORM_SUBMISSION_ERROR', {
        ...submissionAttempt,
        error: error.message
      });

      if (error.message !== 'User cancelled submission due to duplicate check failure') {
        showError("Submission Error", "An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitSuccess = () => {
    fetchFamilies();
    resetForm();
    closeAllModals();
  };

  const closeAllModals = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setShowFamilyDetails(false);
    setShowDuplicateModal(false);
    setSelectedFamily(null);
    setDuplicateData(null);
    setPendingSubmission(null);
  };

  const resetForm = () => {
    setFormData({
      familyName: "",
      familyCode: "",
      primaryMobile: "",
      secondaryMobile: "",
      address: "",
      cnic: "",
      boys: 1,
      girls: 0,
      totalStudents: 1,
    });
    setFormErrors({});
    setFieldValidation({
      cnic: { isValid: false, isChecking: false, lastChecked: null },
      primaryMobile: { isValid: false, isChecking: false, lastChecked: null },
      secondaryMobile: { isValid: false, isChecking: false, lastChecked: null },
    });
    setSelectedFamily(null);
  };

  const handleInputChange = (field, value) => {
    const numericValue = field === "boys" || field === "girls" ? parseInt(value) || 0 : value;
    
    setFormData((prev) => ({
      ...prev,
      [field]: numericValue,
    }));

    if (field === "boys" || field === "girls") {
      const boys = field === "boys" ? numericValue : formData.boys;
      const girls = field === "girls" ? numericValue : formData.girls;
      const totalStudents = boys + girls;
      
      setFormData((prev) => ({
        ...prev,
        totalStudents: totalStudents,
      }));

      if (numericValue < 0) {
        setFormErrors(prev => ({
          ...prev,
          [field]: "Cannot be negative"
        }));
      } else if (boys + girls < 1) {
        setFormErrors(prev => ({
          ...prev,
          boys: "At least one student required",
          girls: "At least one student required"
        }));
      } else {
        if (formErrors.boys === "At least one student required") {
          setFormErrors(prev => ({ ...prev, boys: "" }));
        }
        if (formErrors.girls === "At least one student required") {
          setFormErrors(prev => ({ ...prev, girls: "" }));
        }
      }
    }

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: "" }));
    }

    if (['cnic', 'primaryMobile', 'secondaryMobile'].includes(field)) {
      setFieldValidation(prev => ({
        ...prev,
        [field]: { isValid: false, isChecking: false, lastChecked: null }
      }));
    }
  };

  const handleMobileChange = (field, value) => {
    const digits = toDigitsOnly(value, 11);
    handleInputChange(field, digits);
  };

  const handleSecondaryMobileChange = (value) => {
    const digits = toDigitsOnly(value, 11);
    handleInputChange("secondaryMobile", digits);
  };

  const handleCnicChange = (value) => {
    const digits = toDigitsOnly(value, 13);
    handleInputChange("cnic", digits);
  };

  const handleCounter = (field, increment) => {
    const current = parseInt(formData[field]) || 0;
    const otherField = field === "boys" ? "girls" : "boys";
    const otherValue = parseInt(formData[otherField]) || 0;
    
    let newValue;
    if (increment) {
      if (current >= 50) {
        showWarning("Maximum Limit", "Maximum 50 students per gender allowed.");
        return;
      }
      newValue = current + 1;
    } else {
      if (current <= 0) return;
      
      if (current === 1 && otherValue === 0) {
        showWarning(
          "Minimum Students Required",
          "Family must have at least one student. Cannot remove the last student."
        );
        return;
      }
      
      newValue = current - 1;
    }
    
    handleInputChange(field, newValue);
  };

  const handleGenerateCode = async () => {
    if (showEditForm) {
      showError(
        "Cannot Generate Code",
        "Family code cannot be changed after creation. Please use the existing code."
      );
      return;
    }

    await generateFamilyCode();
  };

  // ============================================
  // FAMILY ACTIONS
  // ============================================
  const handleAddFamily = () => {
    resetForm();
    setShowAddForm(true);
    setShowEditForm(false);
    setShowFamilyDetails(false);
    setShowDuplicateModal(false);
  };

  const handleEditFamily = (family) => {
    setFormData({
      familyName: family.family_name || "",
      familyCode: family.family_code || "",
      primaryMobile: family.primary_mobile || "",
      secondaryMobile: family.secondary_mobile || "",
      address: family.address || "",
      cnic: family.cnic || "",
      boys: family.boys || 1,
      girls: family.girls || 0,
      totalStudents: family.total_students || Math.max(1, (family.boys || 0) + (family.girls || 0)),
    });
    setSelectedFamily(family);
    setShowEditForm(true);
    setShowAddForm(false);
    setShowFamilyDetails(false);
    setShowDuplicateModal(false);
    setShowMoreMenu(null);
  };

  const handleViewFamily = (family) => {
    setSelectedFamily(family);
    setShowFamilyDetails(true);
    fetchFamilyDetails(family.family_code);
    setShowMoreMenu(null);
    
    setShowAddForm(false);
    setShowEditForm(false);
    setShowDuplicateModal(false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchFamilies();
  };

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    fetchFamilies();
  }, []);

  useEffect(() => {
    let result = [...families];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (family) =>
          family.family_name?.toLowerCase().includes(term) ||
          family.family_code?.toLowerCase().includes(term) ||
          family.cnic?.includes(term) ||
          family.primary_mobile?.includes(term) ||
          family.secondary_mobile?.includes(term) ||
          family.address?.toLowerCase().includes(term)
      );
    }

    if (filterStatus !== "all") {
      result = result.filter((family) => {
        const progress = getRegistrationProgress(family);
        if (filterStatus === "complete") return progress === 100;
        if (filterStatus === "incomplete") return progress < 100;
        return true;
      });
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "oldest":
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "name":
          return (a.family_name || "").localeCompare(b.family_name || "");
        case "students":
          return (b.boys + b.girls) - (a.boys + a.girls);
        case "registration":
          return getRegistrationProgress(b) - getRegistrationProgress(a);
        default:
          return 0;
      }
    });

    setFilteredFamilies(result);
    setCurrentPage(1);
  }, [families, searchTerm, filterStatus, sortBy]);

  // ============================================
  // PAGINATION & STATISTICS
  // ============================================
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentFamilies = filteredFamilies.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFamilies.length / itemsPerPage);

  const stats = React.useMemo(() => {
    const total = families.length;
    const totalStudents = families.reduce(
      (sum, f) => sum + (parseInt(f.boys) || 0) + (parseInt(f.girls) || 0),
      0
    );
    const registeredStudents = families.reduce(
      (sum, f) => sum + (parseInt(f.registered_students) || 0),
      0
    );

    const completeCount = families.filter(
      (f) => (f.registered_students || 0) >= ((parseInt(f.boys) || 0) + (parseInt(f.girls) || 0))
    ).length;
    const incompleteCount = families.filter(
      (f) => (f.registered_students || 0) > 0 && (f.registered_students || 0) < ((parseInt(f.boys) || 0) + (parseInt(f.girls) || 0))
    ).length;

    const completionRate =
      totalStudents > 0
        ? Math.round((registeredStudents / totalStudents) * 100)
        : 0;

    return {
      total,
      complete: completeCount,
      incomplete: incompleteCount,
      totalStudents,
      registeredStudents,
      completionRate,
    };
  }, [families]);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderValidationStatus = (field) => {
    const validation = fieldValidation[field];
    if (!validation) return null;

    if (validation.isChecking) {
      return (
        <span className="validation-status checking">
          <FontAwesomeIcon icon={faSync} spin />
          Checking...
        </span>
      );
    }

    if (validation.lastChecked) {
      if (validation.isValid) {
        return (
          <span className="validation-status valid">
            <FontAwesomeIcon icon={faCheckCircle} />
            Valid
          </span>
        );
      } else if (validation.duplicateInfo) {
        return (
          <span className="validation-status duplicate">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            Duplicate Found
          </span>
        );
      }
    }

    return null;
  };

  // ============================================
  // RENDER FAMILY CARD (UPDATED WITH FALLBACK)
  // ============================================
  const renderFamilyCard = (family) => {
    const status = getStatusInfo(family);
    // 🛡️ FIX: Fallback to total_students if boys+girls sum is 0
    const totalMembers = (parseInt(family.boys) || 0) + (parseInt(family.girls) || 0) || parseInt(family.total_students) || 0;

    return (
      <div key={family.id || family.family_code} className="family-card">
        <div className="family-card-header">
          <div className="family-code-badge">
            <FontAwesomeIcon icon={faQrcode} style={{fontSize:'12pt',marginRight:'0.5rem'}} />
            <span style={{fontSize:'13pt'}}>{family.family_code}</span>
          </div>
          <div 
            className="family-status-badge"
            style={{ 
              backgroundColor: status.bgColor,
              color: status.color,
              borderColor: status.color,
              width:'9rem'
            }}
          >
            <FontAwesomeIcon icon={status.icon} style={{marginRight:'.5rem'}}/>
            <span>{status.text}</span>
          </div>
        </div>

        <div className="family-card-body">
          <div className="family-name-section">
            <div className="family-avatar">
              <FontAwesomeIcon icon={faUserGroup} />
            </div>
            <div className="family-name-content">
              <h3 className="family-name">{family.family_name}</h3>
              <p className="family-meta">
                <FontAwesomeIcon icon={faIdCard} />
                <span>{family.cnic ? formatCnicDisplay(family.cnic) : "No CNIC"}</span>
              </p>
            </div>
          </div>

          <div className="family-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUserFriends} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{totalMembers}</div>
                <div className="stat-label">Total Members</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUserCheck} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{family.registered_students || 0}</div>
                <div className="stat-label">Registered</div>
              </div>
            </div>
          </div>

          <div className="family-contact">
            <div className="contact-item">
              <FontAwesomeIcon icon={faPhone} />
              <span>{family.primary_mobile ? formatMobileDisplay(family.primary_mobile) : "No mobile"}</span>
            </div>
            {family.secondary_mobile && family.secondary_mobile.trim() !== "" && (
              <div className="contact-item">
                <FontAwesomeIcon icon={faPhoneAlt} />
                <span>{formatMobileDisplay(family.secondary_mobile)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="family-card-footer">
          <button
            className="details-btn"
            onClick={() => handleViewFamily(family)}
          >
            <FontAwesomeIcon icon={faEye} />
            <span>View Details</span>
          </button>
          <div className="card-actions">
            <button
              className="action-btn edit-btn"
              style={{background:'green'}}
              onClick={() => handleEditFamily(family)}
              title="Edit Family"
            >
              <FontAwesomeIcon icon={faEdit} />
            </button>
            <button
              className="action-btn delete-btn"
              style={{background:'gold'}}
              onClick={() => handleDeleteFamily(family)}
              title="Delete Family"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAddEditForm = () => {
    const isEditing = showEditForm && selectedFamily !== null;
    const showModal = showAddForm || showEditForm;
    const totalStudents = (parseInt(formData.boys) || 0) + (parseInt(formData.girls) || 0);
    const hasMinimumStudents = totalStudents >= 1;

    if (!showModal) return null;

    return (
      <div className={`form-modal-overlay ${showModal ? 'active' : ''}`}>
        <div className="family-form-modal">
          <div className="form-header">
            <h2>
              <FontAwesomeIcon icon={isEditing ? faEdit : faUserPlus} />
              {isEditing ? "Edit Family" : "Add New Family"}
            </h2>
            <button
              className="close-modal-btn"
              onClick={closeAllModals}
              disabled={isSubmitting || isCheckingDuplicates}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="family-form">
            {/* Family Information */}
            <div className="form-section">
              <h3 className="section-title">
                <FontAwesomeIcon icon={faAddressCard} />
                Family Information
              </h3>

              <div className="form-group">
                <label className="form-label required">
                  <FontAwesomeIcon icon={faUserGroup} />
                  Family Name
                </label>
                <input
                  type="text"
                  className={`form-input ${formErrors.familyName ? "error" : ""}`}
                  value={formData.familyName}
                  onChange={(e) =>
                    handleInputChange("familyName", e.target.value)
                  }
                  placeholder="Enter family head's name"
                  disabled={isSubmitting}
                />
                {formErrors.familyName && (
                  <span className="error-message">{formErrors.familyName}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label required">
                  <FontAwesomeIcon icon={faIdCard} />
                  CNIC Number
                </label>
                <div className="validation-wrapper">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`form-input ${formErrors.cnic ? "error" : ""} ${
                      fieldValidation.cnic.isValid ? "valid" : fieldValidation.cnic.duplicateInfo ? "duplicate" : ""
                    }`}
                    value={formatCnicDisplay(formData.cnic)}
                    onChange={(e) => handleCnicChange(e.target.value)}
                    onBlur={(e) => handleFieldBlur('cnic', formData.cnic)}
                    placeholder="34603-7106417-1"
                    maxLength={15}
                    disabled={isSubmitting}
                  />
                  {renderValidationStatus('cnic')}
                </div>
                {formErrors.cnic && (
                  <span className="error-message">{formErrors.cnic}</span>
                )}
                <div className="form-note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  13 digits — dashes are added automatically
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-section">
              <h3 className="section-title">
                <FontAwesomeIcon icon={faPhone} />
                Contact Information
              </h3>

              <div className="form-group">
                <label className="form-label required">
                  <FontAwesomeIcon icon={faPhone} />
                  Primary Mobile
                </label>
                <div className="validation-wrapper">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`form-input ${formErrors.primaryMobile ? "error" : ""} ${
                      fieldValidation.primaryMobile.isValid ? "valid" : fieldValidation.primaryMobile.duplicateInfo ? "duplicate" : ""
                    }`}
                    value={formatMobileDisplay(formData.primaryMobile)}
                    onChange={(e) =>
                      handleMobileChange("primaryMobile", e.target.value)
                    }
                    onBlur={(e) => handleFieldBlur('primaryMobile', formData.primaryMobile)}
                    placeholder="0300-1234567"
                    maxLength={12}
                    disabled={isSubmitting}
                  />
                  {renderValidationStatus('primaryMobile')}
                </div>
                {formErrors.primaryMobile && (
                  <span className="error-message">
                    {formErrors.primaryMobile}
                  </span>
                )}
                <div className="form-note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  11 digits starting with 03 — dashes are added automatically
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faPhoneAlt} />
                  Secondary Mobile (Optional)
                </label>
                <div className="validation-wrapper">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={`form-input ${formErrors.secondaryMobile ? "error" : ""} ${
                      fieldValidation.secondaryMobile.isValid ? "valid" : fieldValidation.secondaryMobile.duplicateInfo ? "duplicate" : ""
                    }`}
                    value={formatMobileDisplay(formData.secondaryMobile)}
                    onChange={(e) =>
                      handleSecondaryMobileChange(e.target.value)
                    }
                    onBlur={(e) => {
                      if (formData.secondaryMobile.trim()) {
                        handleFieldBlur('secondaryMobile', formData.secondaryMobile);
                      }
                    }}
                    placeholder="0300-1234567 (optional)"
                    maxLength={12}
                    disabled={isSubmitting}
                  />
                  {formData.secondaryMobile && renderValidationStatus('secondaryMobile')}
                </div>
                {formErrors.secondaryMobile && (
                  <span className="error-message">{formErrors.secondaryMobile}</span>
                )}
                <div className="form-note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Must be unique if provided
                </div>
              </div>
            </div>

            {/* Family Code Section */}
            <div className="form-section">
              <h3 className="section-title">
                <FontAwesomeIcon icon={faQrcode} />
                Family Identification
              </h3>

              <div className="family-code-section">
                <div className="code-display">
                  <div className="code-label">Family Code:</div>
                  <div
                    className={`code-value ${
                      formData.familyCode ? "generated" : "empty"
                    }`}
                  >
                    {formData.familyCode || "Not Generated"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleGenerateCode}
                  className={`generate-btn ${
                    isGeneratingCode ? "generating" : ""
                  }`}
                  disabled={isGeneratingCode || isEditing || isSubmitting}
                >
                  <FontAwesomeIcon
                    icon={isGeneratingCode ? faRefresh : faMagic}
                    spin={isGeneratingCode}
                  />
                  {isGeneratingCode ? "Generating..." : "Generate Code"}
                </button>
              </div>
              {isEditing && (
                <p className="form-note">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Family code cannot be changed after creation
                </p>
              )}
            </div>

            {/* Address Section */}
            <div className="form-section">
              <h3 className="section-title">
                <FontAwesomeIcon icon={faHome} />
                Family Address
              </h3>

              <div className="form-group">
                <label className="form-label required">
                  <FontAwesomeIcon icon={faLocationDot} />
                  Complete Address
                </label>
                <textarea
                  style={{width:'90%'}}
                  className={`form-textarea ${formErrors.address ? "error" : ""}`}
                  value={formData.address}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  placeholder="Enter complete residential address..."
                  rows="3"
                  disabled={isSubmitting}
                />
                {formErrors.address && (
                  <span className="error-message">{formErrors.address}</span>
                )}
              </div>
            </div>

            {/* Student Information */}
            <div className="form-section">
              <h3 className="section-title">
                <FontAwesomeIcon icon={faUsers} />
                Student Information
              </h3>

              <div className="validation-hint" style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: hasMinimumStudents ? '#d1fae5' : '#fef3c7',
                borderRadius: '5px',
                borderLeft: `4px solid ${hasMinimumStudents ? '#10b981' : '#f59e0b'}`
              }}>
                <FontAwesomeIcon 
                  icon={hasMinimumStudents ? faCheckCircle : faExclamationTriangle} 
                  style={{ 
                    marginRight: '8px', 
                    color: hasMinimumStudents ? '#10b981' : '#f59e0b' 
                  }} 
                />
                <span>
                  {hasMinimumStudents 
                    ? `Family has ${totalStudents} student(s) - Requirement satisfied`
                    : 'Family must have at least one student (boy or girl)'
                  }
                </span>
              </div>

              <div className="counters-grid">
                <div className="counter-card" style={{
                  border: !hasMinimumStudents && formData.boys === 0 ? '2px solid #dc3545' : '1px solid #dee2e6',
                  backgroundColor: !hasMinimumStudents && formData.boys === 0 ? 'rgba(220, 53, 69, 0.05)' : 'transparent'
                }}>
                  <div className="counter-header">
                    <FontAwesomeIcon icon={faMale} />
                    <span>Boys</span>
                  </div>
                  <div className="counter-controls">
                    <button
                      type="button"
                      onClick={() => handleCounter("boys", false)}
                      className="counter-btn"
                      disabled={isSubmitting || formData.boys === 0}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      className="counter-input"
                      value={formData.boys}
                      onChange={(e) =>
                        handleInputChange("boys", parseInt(e.target.value) || 0)
                      }
                      min="0"
                      max="50"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => handleCounter("boys", true)}
                      className="counter-btn"
                      disabled={isSubmitting}
                    >
                      +
                    </button>
                  </div>
                  {formErrors.boys && (
                    <div className="student-validation-error">
                      <FontAwesomeIcon icon={faExclamationTriangle} /> {formErrors.boys}
                    </div>
                  )}
                </div>

                <div className="counter-card" style={{
                  border: !hasMinimumStudents && formData.girls === 0 ? '2px solid #dc3545' : '1px solid #dee2e6',
                  backgroundColor: !hasMinimumStudents && formData.girls === 0 ? 'rgba(220, 53, 69, 0.05)' : 'transparent'
                }}>
                  <div className="counter-header">
                    <FontAwesomeIcon icon={faFemale} />
                    <span>Girls</span>
                  </div>
                  <div className="counter-controls">
                    <button
                      type="button"
                      onClick={() => handleCounter("girls", false)}
                      className="counter-btn"
                      disabled={isSubmitting || formData.girls === 0}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      className="counter-input"
                      value={formData.girls}
                      onChange={(e) =>
                        handleInputChange("girls", parseInt(e.target.value) || 0)
                      }
                      min="0"
                      max="50"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => handleCounter("girls", true)}
                      className="counter-btn"
                      disabled={isSubmitting}
                    >
                      +
                    </button>
                  </div>
                  {formErrors.girls && (
                    <div className="student-validation-error">
                      <FontAwesomeIcon icon={faExclamationTriangle} /> {formErrors.girls}
                    </div>
                  )}
                </div>

                <div className="counter-card total-card" style={{
                  border: hasMinimumStudents ? '1px solid #dee2e6' : '2px solid #dc3545',
                  backgroundColor: hasMinimumStudents ? 'transparent' : 'rgba(220, 53, 69, 0.05)'
                }}>
                  <div className="counter-header">
                    <FontAwesomeIcon icon={faUsers} />
                    <span>Total Students</span>
                  </div>
                  <div className="total-value" style={{
                    color: hasMinimumStudents ? '#343a40' : '#dc3545',
                    fontSize: hasMinimumStudents ? '1.5rem' : '1.3rem'
                  }}>
                    {formData.totalStudents}
                    {!hasMinimumStudents && (
                      <span style={{
                        fontSize: '0.8em',
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '5px',
                        fontWeight: 'normal'
                      }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} /> Minimum 1 required
                      </span>
                    )}
                    {formData.totalStudents > 100 && (
                      <span style={{
                        fontSize: '0.8em',
                        color: '#dc3545',
                        display: 'block',
                        marginTop: '5px',
                        fontWeight: 'normal'
                      }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} /> Maximum 100 exceeded
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
                disabled={isSubmitting || isCheckingDuplicates}
              >
                <FontAwesomeIcon icon={faRefresh} /> Reset
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || isCheckingDuplicates || !hasMinimumStudents || formData.totalStudents > 100}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSync} spin />
                    {isCheckingDuplicates ? "Checking Duplicates..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={isEditing ? faSave : faCheck} />
                    {isEditing ? "Update Family" : "Register Family"}
                  </>
                )}
              </button>
            </div>

            {/* Duplicate Prevention Status */}
            <div className="duplicate-prevention-status">
              <div className="duplicate-status-header">
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>Duplicate Prevention Status</span>
              </div>
              <div className="duplicate-status-grid">
                <div className="status-item">
                  <span className="status-label">CNIC:</span>
                  <span className={`status-value ${
                    fieldValidation.cnic.isValid ? 'valid' : 
                    fieldValidation.cnic.duplicateInfo ? 'duplicate' : 
                    formData.cnic ? 'pending' : 'empty'
                  }`}>
                    {fieldValidation.cnic.isValid ? '✓ Valid' : 
                     fieldValidation.cnic.duplicateInfo ? '✗ Duplicate' : 
                     formData.cnic ? 'Checking...' : 'Not checked'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Primary Mobile:</span>
                  <span className={`status-value ${
                    fieldValidation.primaryMobile.isValid ? 'valid' : 
                    fieldValidation.primaryMobile.duplicateInfo ? 'duplicate' : 
                    formData.primaryMobile ? 'pending' : 'empty'
                  }`}>
                    {fieldValidation.primaryMobile.isValid ? '✓ Valid' : 
                     fieldValidation.primaryMobile.duplicateInfo ? '✗ Duplicate' : 
                     formData.primaryMobile ? 'Checking...' : 'Not checked'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Secondary Mobile:</span>
                  <span className={`status-value ${
                    !formData.secondaryMobile ? 'empty' :
                    fieldValidation.secondaryMobile.isValid ? 'valid' : 
                    fieldValidation.secondaryMobile.duplicateInfo ? 'duplicate' : 
                    'pending'
                  }`}>
                    {!formData.secondaryMobile ? 'Empty' :
                     fieldValidation.secondaryMobile.isValid ? '✓ Valid' : 
                     fieldValidation.secondaryMobile.duplicateInfo ? '✗ Duplicate' : 
                     'Checking...'}
                  </span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ============================================
  // FAMILY DETAILS MODAL
  // ============================================
  const renderFamilyDetailsModal = () => {
    if (!showFamilyDetails || !selectedFamily) return null;

    const status = getStatusInfo(selectedFamily);
    const progress = getRegistrationProgress(selectedFamily);
    const totalMembers = (parseInt(selectedFamily.boys) || 0) + (parseInt(selectedFamily.girls) || 0);

    return (
      <div className={`modal-overlay ${showFamilyDetails ? 'active' : ''}`}>
        <div className="family-details-modal">
          <div className="modal-header">
            <div className="header-content">
              <h2>
                <FontAwesomeIcon icon={faUsers} />
                Family Details
              </h2>
              <button
                className="close-modal"
                onClick={closeAllModals}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="modal-body">
            {/* Family Overview */}
            <div className="family-overview">
              <div className="family-header">
                <div className="family-title">
                  <div className="family-avatar-large">
                    <FontAwesomeIcon icon={faUserGroup} />
                  </div>
                  <div className="family-info">
                    <h3>{selectedFamily.family_name}</h3>
                    <div className="family-code-display">
                      <FontAwesomeIcon icon={faQrcode} />
                      <span>{selectedFamily.family_code}</span>
                    </div>
                  </div>
                </div>
                <div 
                  className="status-indicator"
                  style={{ 
                    backgroundColor: status.bgColor,
                    color: status.color,
                    borderColor: status.color,
                    width:'9rem',
                    borderRadius:'1rem',
                  }}
                >
                  <FontAwesomeIcon icon={status.icon} />
                  <span>{status.text}</span>
                </div>
              </div>

              {/* Registration Progress */}
              <div className="progress-section">
                <div className="progress-header">
                  <span>Registration Progress</span>
                  <span>{progress}%</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${progress}%`,
                      backgroundColor: status.color
                    }}
                  ></div>
                </div>
                <div className="progress-text">
                  {selectedFamily.registered_students || 0} out of {totalMembers} students registered
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="details-grid">
              {/* Contact Information */}
              <div className="detail-section">
                <h4>
                  <FontAwesomeIcon icon={faPhone} />
                  Contact Information
                </h4>
                
                <div className="detail-item">
                  <FontAwesomeIcon icon={faIdCard} />
                  <div>
                    <label>CNIC</label>
                    <span>{selectedFamily.cnic ? formatCnicDisplay(selectedFamily.cnic) : "Not provided"}</span>
                  </div>
                </div>
                
                <div className="detail-item">
                  <FontAwesomeIcon icon={faPhone} />
                  <div>
                    <label>Primary Mobile</label>
                    <span>{selectedFamily.primary_mobile ? formatMobileDisplay(selectedFamily.primary_mobile) : "Not provided"}</span>
                  </div>
                </div>
                
                {selectedFamily.secondary_mobile && selectedFamily.secondary_mobile.trim() !== "" && (
                  <div className="detail-item">
                    <FontAwesomeIcon icon={faPhoneAlt} />
                    <div>
                      <label>Secondary Mobile</label>
                      <span>{formatMobileDisplay(selectedFamily.secondary_mobile)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Family Statistics */}
              <div className="detail-section">
                <h4>
                  <FontAwesomeIcon icon={faChartLine} />
                  Family Statistics
                </h4>
                
                <div className="student-stats-grid">
                  <div className="student-stat">
                    <div className="student-stat-icon boys">
                      <FontAwesomeIcon icon={faMale} />
                    </div>
                    <div className="student-stat-info">
                      <div className="student-stat-value">{selectedFamily.boys || 0}</div>
                      <div className="student-stat-label">Boys</div>
                    </div>
                  </div>
                  
                  <div className="student-stat">
                    <div className="student-stat-icon girls">
                      <FontAwesomeIcon icon={faFemale} />
                    </div>
                    <div className="student-stat-info">
                      <div className="student-stat-value">{selectedFamily.girls || 0}</div>
                      <div className="student-stat-label">Girls</div>
                    </div>
                  </div>
                  
                  <div className="student-stat">
                    <div className="student-stat-icon total">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="student-stat-info">
                      <div className="student-stat-value">{totalMembers}</div>
                      <div className="student-stat-label">Total Students</div>
                    </div>
                  </div>
                </div>
                
                <div className="detail-item" style={{ marginTop: '20px' }}>
                  <FontAwesomeIcon icon={faUserCheck} />
                  <div>
                    <label>Registered Students</label>
                    <span>{selectedFamily.registered_students || 0}</span>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="detail-section">
                <h4>
                  <FontAwesomeIcon icon={faHome} />
                  Address Information
                </h4>
                
                <div className="detail-item full-width">
                  <FontAwesomeIcon icon={faLocationDot} />
                  <div style={{ flex: 1 }}>
                    <label>Complete Address</label>
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFamily.address || "No address provided"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="detail-section">
                <h4>
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Additional Information
                </h4>
                
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCalendar} />
                  <div>
                    <label>Created Date</label>
                    <span>
                      {selectedFamily.created_at 
                        ? new Date(selectedFamily.created_at).toLocaleDateString()
                        : "Unknown"
                      }
                    </span>
                  </div>
                </div>
                
                <div className="detail-item">
                  <FontAwesomeIcon icon={faClock} />
                  <div>
                    <label>Last Updated</label>
                    <span>
                      {selectedFamily.updated_at 
                        ? new Date(selectedFamily.updated_at).toLocaleDateString()
                        : "Never"
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Students List */}
            {familyStudents.length > 0 && (
              <div className="students-section">
                <h4>
                  <FontAwesomeIcon icon={faUserFriends} />
                  Family Students ({familyStudents.length})
                </h4>
                
                <div className="students-list">
                  <table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Gender</th>
                        <th>Age</th>
                        <th>Class</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {familyStudents.map((student, index) => (
                        <tr key={student.id || index}>
                          <td>{student.name || "Unnamed"}</td>
                          <td>
                            <span className={`gender-badge ${student.gender === 'male' ? 'male' : 'female'}`}>
                              <FontAwesomeIcon icon={student.gender === 'male' ? faMars : faVenus} />
                              {student.gender === 'male' ? 'Male' : 'Female'}
                            </span>
                          </td>
                          <td>{student.age || "N/A"}</td>
                          <td>{student.class || "N/A"}</td>
                          <td>
                            <span style={{
                              color: student.status === 'active' ? 'var(--success-color)' : 
                                     student.status === 'inactive' ? 'var(--warning-color)' : 'var(--gray-500)',
                              fontWeight: '600',
                              fontSize: '0.85rem'
                            }}>
                              {student.status || "unknown"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={closeAllModals}
            >
              <FontAwesomeIcon icon={faTimes} />
              Close
            </button>
            <button
              className="btn btn-primary"
              onClick={() => handleEditFamily(selectedFamily)}
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit Family
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDeleteFamily(selectedFamily)}
            >
              <FontAwesomeIcon icon={faTrash} />
              Delete Family
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="family-manager-wrapper">
      <Sidebar />

      <main className="family-manager-main">
        {/* Header Section */}
        <div className="family-header-section">
          <div className="header-content">
            <div className="header-icon-wrapper">
              <FontAwesomeIcon icon={faUsersCog} className="header-main-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">
                <span className="title-gradient">Family Management</span>
              </h1>
              <p className="header-subtitle">
                Manage all families, view details, and register new families
              </p>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-icon total">
                <FontAwesomeIcon icon={faDatabase} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Families</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon students">
                <FontAwesomeIcon icon={faChild} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.totalStudents}</div>
                <div className="stat-label">Total Students</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon completion">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{stats.completionRate}%</div>
                <div className="stat-label">Completion Rate</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon registered">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <div className="stat-content">
                <div className="stat-value">
                  {stats.registeredStudents}/{stats.totalStudents}
                </div>
                <div className="stat-label">Registered</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="family-manager-content">
          {/* Family List Panel */}
          <div className="family-list-panel">
            {/* Controls Header */}
            <div className="list-controls">
              <div className="search-section">
                <div className="search-box">
                  <FontAwesomeIcon icon={faSearch} className="search-icon" 
                  style={{left:'-5px'}}/>
                  <input
                    type="text"
                    placeholder="Search families by name, code, CNIC, or mobile..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm("")}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>

                <div className="filter-controls">
                  <div className="filter-group">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="status-filter"
                    >
                      <option value="all">All Families</option>
                      <option value="complete">Complete Registration</option>
                      <option value="incomplete">
                        Incomplete Registration
                      </option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="sort-select"
                    >
                      <option value="newest">Newest First</option>
                      <option value="oldest">Oldest First</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="students">Most Students</option>
                      <option value="registration">
                        Registration Progress
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button
                  className="btn refresh-btn"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  title="Refresh data"
                >
                  <FontAwesomeIcon icon={faSync} spin={isRefreshing} />
                  {isRefreshing ? "Refreshing..." : "Refresh"}
                </button>
                <button
                  className="add-family-btn"
                  onClick={handleAddFamily}
                  title="Add new family"
                >
                  <FontAwesomeIcon icon={faUserPlus} />
                  <span>Add New Family</span>
                </button>
              </div>
            </div>

            {/* Family Grid */}
            <div className="family-grid">
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading families...</p>
                </div>
              ) : currentFamilies.length > 0 ? (
                currentFamilies.map(renderFamilyCard)
              ) : (
                <div className="no-families">
                  <FontAwesomeIcon icon={faUsers} className="no-data-icon" />
                  <h3>No families found</h3>
                  <p>
                    {searchTerm || filterStatus !== "all"
                      ? "Try different search terms or filters"
                      : "Add your first family to get started"}
                  </p>
                  <button
                    className="add-first-family-btn"
                    onClick={handleAddFamily}
                  >
                    <FontAwesomeIcon icon={faPlusCircle} />
                    Add First Family
                  </button>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  disabled={currentPage === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  Previous
                </button>

                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`page-number ${
                          currentPage === pageNum ? "active" : ""
                        }`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="pagination-btn"
                  onClick={() =>
                    setCurrentPage((page) => Math.min(totalPages, page + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add/Edit Form Modal */}
      {renderAddEditForm()}

      {/* Family Details Modal */}
      {renderFamilyDetailsModal()}

      {/* Duplicate Prevention Modal */}
      <DuplicatePreventionModal
        duplicates={duplicateData}
        onConfirm={handleDuplicateModalConfirm}
        onCancel={handleDuplicateModalCancel}
        onEditExisting={handleEditExistingFamily}
      />
    </div>
  );
};

export default FamilyManager;